import { formatHblBlNo } from './hblFormat';

export function isDoorToDoor(clearingType) {
  const s = String(clearingType || '').toLowerCase().replace(/\s+/g, '_');
  return s === 'door_to_door' || s === 'd2d';
}

export function isUpbOrGift(clearingType) {
  const s = String(clearingType || '').toLowerCase();
  return s === 'upb' || s === 'gift';
}

export function isConsolidatedParent(collection) {
  return Array.isArray(collection?.childCollectionIds) && collection.childCollectionIds.length > 0;
}

function boxQty(b) {
  const q = Number.parseInt(String(b?.quantity), 10);
  return Number.isFinite(q) && q > 0 ? q : 1;
}

export function formatPackageCode(collectionNo, subNo) {
  const no = String(collectionNo || '').trim();
  const sub = String(subNo || '').padStart(2, '0');
  return `${no}-${sub}`.toUpperCase();
}

export function formatConsigneeBlock(consignee) {
  const name = String(consignee?.nameWithInitials || consignee?.name || '').trim() || '—';
  const address = String(consignee?.address || '').trim() || '—';
  let phone = '';
  if (Array.isArray(consignee?.mobiles)) {
    phone = consignee.mobiles.filter(Boolean).join(' / ');
  } else if (consignee?.mobiles) {
    phone = String(consignee.mobiles);
  }
  return { name, address, phone: phone || '—' };
}

function expandBoxesToRows(collection, context) {
  const boxes = Array.isArray(collection?.boxes) ? collection.boxes : [];
  const collectionNo = String(collection?.collectionNo || collection?._id || '').trim();
  const expanded = [];

  boxes.forEach((b, boxIdx) => {
    const qty = boxQty(b);
    for (let u = 0; u < qty; u += 1) {
      expanded.push({ b, boxIdx, u });
    }
  });

  return expanded.map((item, globalIdx) => {
    const subNo = String(globalIdx + 1);
    const { b } = item;
    return {
      key: `${collection?._id || collectionNo}-${item.boxIdx}-${item.u}`,
      subNo,
      packageCode: formatPackageCode(collectionNo, subNo),
      hblDisplay: context.hblDisplay,
      hblSourceNo: context.hblSourceNo,
      template: context.template,
      cargoType: String(b?.boxType || b?.officialBoxName || '').trim(),
      description: String(b?.description || '').trim(),
      consigneeName: context.consigneeBlock?.name,
      consigneeAddress: context.consigneeBlock?.address,
      consigneePhone: context.consigneeBlock?.phone,
      parentHblNo: context.parentHblNo || '',
      sourceCollectionNo: collectionNo,
    };
  });
}

function expandConsolidatedParent(parent, childCollections, context) {
  const rows = [];
  let globalSubNo = 0;
  const childIds = (parent?.childCollectionIds || []).map(String);

  for (const childId of childIds) {
    const child = childCollections.find((c) => String(c?._id || c?.id) === childId);
    if (!child) continue;
    const boxes = Array.isArray(child.boxes) ? child.boxes : [];
    const expanded = [];
    boxes.forEach((b, boxIdx) => {
      const qty = boxQty(b);
      for (let u = 0; u < qty; u += 1) {
        expanded.push({ b, boxIdx, u });
      }
    });
    for (const item of expanded) {
      globalSubNo += 1;
      const subNo = String(globalSubNo);
      const collectionNo = String(parent?.collectionNo || parent?._id || '').trim();
      const { b } = item;
      rows.push({
        key: `${parent?._id || collectionNo}-${childId}-${item.boxIdx}-${item.u}`,
        subNo,
        packageCode: formatPackageCode(collectionNo, subNo),
        hblDisplay: context.hblDisplay,
        hblSourceNo: context.hblSourceNo,
        template: 'standard',
        cargoType: String(b?.boxType || b?.officialBoxName || '').trim(),
        description: String(b?.description || '').trim(),
        parentHblNo: '',
        sourceCollectionNo: collectionNo,
        childD2dNo: String(child.collectionNo || '').trim(),
      });
    }
  }
  return rows;
}

/**
 * @param {object} collection
 * @param {object} [opts]
 * @param {object} [opts.parentCollection]
 * @param {object} [opts.consignee]
 * @param {object[]} [opts.childCollections]
 */
export function getStickerContext(collection, opts = {}) {
  const { parentCollection, consignee, childCollections = [] } = opts;

  if (isConsolidatedParent(collection)) {
    const hblSourceNo = String(collection.collectionNo || '').trim();
    return {
      template: 'standard',
      hblDisplay: formatHblBlNo(hblSourceNo),
      hblSourceNo,
      parentHblNo: '',
      consigneeBlock: null,
      canPrint: true,
      blockReason: '',
      childCollections,
    };
  }

  if (isDoorToDoor(collection?.clearingType)) {
    if (!parentCollection?.collectionNo) {
      return {
        template: 'doorToDoor',
        hblDisplay: '—',
        hblSourceNo: '',
        parentHblNo: '',
        consigneeBlock: formatConsigneeBlock(consignee),
        canPrint: false,
        blockReason: 'Link this door-to-door bill to a consolidated UPB/Gift bill before printing stickers.',
      };
    }
    const parentHblNo = String(parentCollection.collectionNo).trim();
    return {
      template: 'doorToDoor',
      hblDisplay: formatHblBlNo(parentHblNo),
      hblSourceNo: parentHblNo,
      parentHblNo,
      consigneeBlock: formatConsigneeBlock(consignee),
      canPrint: true,
      blockReason: '',
    };
  }

  const hblSourceNo = String(collection.collectionNo || '').trim();
  return {
    template: 'standard',
    hblDisplay: formatHblBlNo(hblSourceNo),
    hblSourceNo,
    parentHblNo: '',
    consigneeBlock: null,
    canPrint: true,
    blockReason: '',
  };
}

export function buildPackageRows(collection, context) {
  if (isConsolidatedParent(collection)) {
    return expandConsolidatedParent(collection, context.childCollections || [], context);
  }
  return expandBoxesToRows(collection, context);
}

export function duplicateForStickers(rows, copies = 2) {
  const out = [];
  for (const row of rows) {
    for (let i = 0; i < copies; i += 1) {
      out.push({ ...row, copyIndex: i + 1 });
    }
  }
  return out;
}

export function countPackages(rows) {
  const unique = new Set(rows.map((r) => r.key));
  return unique.size || rows.length;
}

export function stickerSummary(collection, context) {
  const rows = buildPackageRows(collection, context);
  const packages = countPackages(rows);
  return {
    packages,
    stickers: packages * 2,
    rows,
    template: context.template,
  };
}

export function canPrintStickers(collection, collectionsPool = []) {
  if (isConsolidatedParent(collection)) {
    return { ok: true, reason: '' };
  }
  if (isDoorToDoor(collection?.clearingType)) {
    const parentId = collection?.consolidatedInto ? String(collection.consolidatedInto) : '';
    if (!parentId) {
      return { ok: false, reason: 'Link to consolidated UPB/Gift bill first' };
    }
    const parent = collectionsPool.find((c) => String(c?._id || c?.id) === parentId);
    if (!parent?.collectionNo) {
      return { ok: false, reason: 'Consolidated parent bill not found' };
    }
    return { ok: true, reason: '' };
  }
  return { ok: true, reason: '' };
}
